import React from 'react';
import MenuItem from '@mui/material/MenuItem';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import { Typography, Grid, Chip, useTheme, Box, Divider, List, ListItem } from '@mui/material';
import { tokens } from '../../../theme';
import { formattedDate, isEmpty } from '../../../Utils/Functions';
import MuiDialog from '../../../SystemDesign/MuiDialog';
import InfoIcon from '@mui/icons-material/Info';
import { DialogSection, DialogSectionLabel, DialogSectionValue } from '../../../SystemDesign/Styles';
import PersonIcon from '@mui/icons-material/Person';

/**
 * SeeMorePoste Component - Shows detailed information about a poste
 * @param {Object} props
 * @param {Object} props.row - The row object containing the poste data
 */
const SeeMorePoste = ({ row }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  
  // Get the poste data from the row
  const poste = row?.original;
  
  if (!poste) return null;

  // Define sections configuration
  const sections = [
    {
      id: 'basic',
      title: 'Basic Information',
      icon: <InfoIcon sx={{ mr: 1, fontSize: '1.2rem' }} />,
      content: [
        { label: 'Name', value: poste.name },
        { label: 'Created At', value: formattedDate(new Date(poste.createdAt)) },
        { label: 'Last Updated', value: formattedDate(new Date(poste.updatedAt)) },
        { 
          label: 'Created By', 
          value: isEmpty(poste.createdBy) ? 'Unknown' : 
            (poste.createdBy.username || poste.createdBy.email || 'Unknown')
        }
      ]
    },
    {
      id: 'users',
      title: 'Associated Users',
      icon: <PersonIcon sx={{ mr: 1, fontSize: '1.2rem' }} />,
      content: [
        {
          label: '',
          value: '',
          custom: (
            Array.isArray(poste.users) && poste.users.length > 0 ? (
              <List dense>
                {poste.users.map((user, index) => (
                  <ListItem key={user.id || index} sx={{ pl: 0 }}>
                    <Box 
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between', 
                        alignItems: 'center',
                        width: '100%'
                      }}
                    >
                      <Typography variant="body2">
                        {user.username || user.email || 'Unknown User'}
                        {user.email && user.username && (
                          <Typography variant="caption" display="block" color="text.secondary">
                            {user.email}
                          </Typography>
                        )}
                      </Typography>
                      {user.isActive && (
                        <Chip 
                          label="Active" 
                          size="small" 
                          color="success" 
                          variant="outlined" 
                        />
                      )}
                    </Box>
                  </ListItem>
                ))}
              </List>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No users associated with this poste.
              </Typography>
            )
          )
        }
      ]
    }
  ];

  // Reusable components
  const DetailItem = ({ label, value, chip, icon, custom }) => (
    <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 0.5 }}>
      {label && (
        <Typography sx={DialogSectionLabel(colors)}>
          {icon && React.cloneElement(icon, { fontSize: 'small' })}
          {label}:
        </Typography>
      )}
      {chip ? (
        <Box sx={{ '& .MuiChip-root': { transition: 'all 0.2s', '&:hover': { opacity: 0.8 } } }}>
          {chip}
        </Box>
      ) : custom ? (
        custom
      ) : (
        <Typography sx={DialogSectionValue(colors)}>{value || '-'}</Typography>
      )}
    </Box>
  );

  const SectionHeader = ({ title, icon }) => (
    <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', my: 2 }}>
      <Typography sx={{ ...DialogSection(colors), mr: 1 }}>
        {icon}{title}
      </Typography>
      <Divider sx={{ flexGrow: 1 }} />
    </Box>
  );

  const dialogContent = (
    <Grid container direction="column" spacing={1}>
      {sections.map((section) => (
        <Grid item size={{ xs: 12 }} key={section.id}>
          <SectionHeader title={section.title} icon={section.icon} />
          {section.content.map((item, index) => (
            <React.Fragment key={index}>
              {item.grid ? (
                <Grid container direction="column" spacing={2}>
                  {item.grid.map((gridItem, gridIndex) => (
                    <Grid item size={{ xs: 12 }} key={gridIndex}>
                      <DetailItem {...gridItem} />
                    </Grid>
                  ))}
                </Grid>
              ) : (
                <DetailItem {...item} />
              )}
            </React.Fragment>
          ))}
        </Grid>
      ))}
    </Grid>
  );

  const triggerButton = (
    <MenuItem>
      <ListItemIcon>
        <VisibilityIcon fontSize="small" />
      </ListItemIcon>
      <ListItemText>See More</ListItemText>
    </MenuItem>
  );

  return (
    <MuiDialog
      icon={<InfoIcon />}
      triggerButton={triggerButton}
      title="Position Information"
      subtitle={`Detailed information for Position: ${poste.name}`}
      content={dialogContent}
      existAction={false}
      maxWidth="md"
    />
  );
};

export default SeeMorePoste;
